<template>
  <div class="an-page">
    <!-- header: chosen by the route name (users-analysis) -->
    <MainHeader />
    <slot name="tabs" />

    <!-- KPIs -->
    <div class="an-kpis">
      <analysis-stat-card
        v-for="kpi in kpis"
        :key="kpi.label"
        v-bind="kpi"
      />
    </div>

    <!-- filters -->
    <div class="ua-filters">
      <div class="ua-filters__row">
        <div class="an-search an-search--inset">
          <feather-icon
            icon="SearchIcon"
            size="16"
          />
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Search by engineer name..."
          >
          <button
            v-if="searchQuery"
            type="button"
            class="an-search__clear"
            aria-label="Clear search"
            @click="searchQuery = ''"
          >
            <feather-icon
              icon="XIcon"
              size="14"
            />
          </button>
        </div>

        <button
          type="button"
          class="an-btn"
          :class="{ 'an-btn--danger-active': filterLateRequests }"
          @click="filterLateRequests = !filterLateRequests"
        >
          <feather-icon
            icon="AlertTriangleIcon"
            size="15"
          />
          Late requests only
        </button>

        <button
          v-if="hasActiveFilters"
          type="button"
          class="an-btn"
          @click="resetFilters"
        >
          <feather-icon
            icon="RefreshCwIcon"
            size="14"
          />
          Reset
        </button>
      </div>

      <div class="ua-filters__row">
        <span class="ua-filters__label">
          <feather-icon
            icon="FilterIcon"
            size="14"
          />
          Sources
        </span>
        <button
          v-for="source in availableSources"
          :key="source.key"
          type="button"
          class="an-pill an-pill--tinted"
          :class="{ 'an-pill--active': selectedSources.includes(source.key) }"
          :style="{ '--accent': source.theme.color, '--accent-soft': source.soft }"
          @click="toggleSource(source.key)"
        >
          <feather-icon
            :icon="source.theme.icon"
            size="13"
          />
          {{ source.theme.label }}
        </button>
      </div>
    </div>

    <!-- table -->
    <div
      v-if="filteredItems.length"
      class="ua-table-wrap"
    >
      <table class="ua-table">
        <thead>
          <tr>
            <th class="col-index">
              #
            </th>
            <th
              v-for="col in columns"
              :key="col.key"
              :class="['sortable', col.class, { 'is-sorted': sortKey === col.key }]"
              @click="setSort(col.key)"
            >
              <span class="th-inner">
                <feather-icon
                  :icon="col.icon"
                  size="14"
                />
                {{ col.label }}
                <span
                  v-if="sortKey === col.key"
                  class="th-arrow"
                >
                  <feather-icon
                    :icon="sortDir === 'desc' ? 'ArrowDownIcon' : 'ArrowUpIcon'"
                    size="13"
                  />
                </span>
              </span>
            </th>
            <th>
              <span class="th-inner">
                <feather-icon
                  icon="GridIcon"
                  size="14"
                />
                Sources
              </span>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="(item, index) in filteredItems"
            :key="`${item.name}-${index}`"
          >
            <td class="col-index">
              {{ index + 1 }}
            </td>

            <td>
              <div class="ua-user">
                <span
                  class="ua-avatar"
                  :style="{ background: avatarColor(item.name) }"
                >{{ initials(item.name) }}</span>
                <span class="ua-user__name">{{ item.name }}</span>
              </div>
            </td>

            <td class="col-load">
              <div class="ua-load">
                <span class="ua-load__value">{{ format(item.load) }}</span>
                <div class="ua-load__track">
                  <div
                    class="ua-load__bar"
                    :class="`ua-load__bar--${loadLevel(item.load)}`"
                    :style="{ width: loadWidth(item.load) }"
                  />
                </div>
              </div>
            </td>

            <td class="col-num">
              <span class="ua-num ua-num--pending">
                <feather-icon
                  icon="ClockIcon"
                  size="13"
                />
                {{ format(item.pending) }}
              </span>
            </td>

            <td class="col-num">
              <span
                class="ua-num"
                :class="item.late > 0 ? 'ua-num--late' : 'ua-num--zero'"
              >
                <feather-icon
                  icon="AlertTriangleIcon"
                  size="13"
                />
                {{ format(item.late) }}
              </span>
            </td>

            <td>
              <div class="ua-sources">
                <button
                  v-for="(value, key) in item.sources"
                  :key="key"
                  type="button"
                  class="ua-source"
                  :style="sourceStyle(key)"
                  :title="`${themeOf(key).label} - ${format(value.total_count)} requests`"
                  @click="showSourceData(item.name, key, value)"
                >
                  <feather-icon
                    :icon="themeOf(key).icon"
                    size="13"
                  />
                  {{ themeOf(key).label }}
                  <span
                    v-if="toNum(value.late_requests) > 0"
                    class="ua-source__late"
                  />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <div
      v-else
      class="an-empty"
    >
      <feather-icon
        icon="UsersIcon"
        size="40"
      />
      <h5>No engineers match your filters</h5>
      <p>Try changing the search or the selected sources.</p>
    </div>

    <p class="ua-footnote">
      Showing {{ filteredItems.length }} of {{ items.length }} engineers
    </p>

    <source-detail-modal
      :visible.sync="isModalVisible"
      :source="sourceData.source"
      :subtitle="sourceData.name"
      :data="sourceData.data"
    />
  </div>
</template>

<script>
import AnalysisStatCard from '@/components/analysis-table/AnalysisStatCard.vue'
import SourceDetailModal from '@/components/analysis-table/SourceDetailModal.vue'
import MainHeader from '@/components/main-header/MainHeader.vue'
import {
  getSourceTheme, tint, toNumber, formatNumber, palette,
} from '@/components/analysis-table/analysisTheme'

const AVATAR_COLORS = ['#1c84f2', '#0a62e8', '#0fb5c9', '#3a54c4', '#34b3f0', '#6a63f0', '#13a98b', '#4a6fa5']

export default {
  components: {
    MainHeader,
    AnalysisStatCard,
    SourceDetailModal,
  },
  props: {
    userAnalysisData: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      isModalVisible: false,
      sourceData: { name: '', source: '', data: {} },
      searchQuery: '',
      selectedSources: [],
      filterLateRequests: false,
      sortKey: 'load',
      sortDir: 'desc',
      columns: [
        {
          key: 'name', label: 'Engineer', icon: 'UserIcon', class: 'col-name',
        },
        {
          key: 'load', label: 'Load', icon: 'ActivityIcon', class: 'col-load',
        },
        {
          key: 'pending', label: 'Pending', icon: 'ClockIcon', class: 'col-num',
        },
        {
          key: 'late', label: 'Late', icon: 'AlertTriangleIcon', class: 'col-num',
        },
      ],
    }
  },
  computed: {
    items() {
      return (this.userAnalysisData || []).map(data => {
        const sources = data.sources || {}
        return {
          name: data.engineer_name || '',
          load: toNumber(data.total_load),
          pending: toNumber(data.total_pending),
          late: Object.values(sources).reduce((acc, s) => acc + toNumber(s.late_requests), 0),
          sources,
        }
      })
    },
    maxLoad() {
      return Math.max(1, ...this.items.map(i => i.load))
    },
    availableSources() {
      const keys = new Set()
      this.items.forEach(item => Object.keys(item.sources).forEach(k => keys.add(k)))

      return [...keys]
        .map(key => {
          const theme = getSourceTheme(key)
          return { key, theme, soft: tint(theme.color, 0.14) }
        })
        .sort((a, b) => a.theme.label.localeCompare(b.theme.label))
    },
    hasActiveFilters() {
      return Boolean(this.searchQuery || this.selectedSources.length || this.filterLateRequests)
    },
    filteredItems() {
      const query = this.searchQuery.trim().toLowerCase()
      let filtered = this.items.filter(item => item.name.toLowerCase().includes(query))

      if (this.selectedSources.length) {
        filtered = filtered.filter(item => this.selectedSources.some(source => item.sources[source]))
      }

      if (this.filterLateRequests) {
        filtered = filtered.filter(item => item.late > 0)
      }

      const dir = this.sortDir === 'desc' ? -1 : 1
      const key = this.sortKey

      return filtered.sort((a, b) => {
        if (key === 'name') return a.name.localeCompare(b.name) * dir
        return (a[key] - b[key]) * dir
      })
    },
    kpis() {
      const sum = key => this.items.reduce((acc, i) => acc + i[key], 0)

      return [
        {
          label: 'Engineers', value: this.items.length, icon: 'UsersIcon', color: palette.primary,
        },
        {
          label: 'Total Load', value: sum('load'), icon: 'ActivityIcon', color: '#0a62e8', hint: 'requests in turn',
        },
        {
          label: 'Total Pending', value: sum('pending'), icon: 'ClockIcon', color: palette.warning,
        },
        {
          label: 'Late Requests', value: sum('late'), icon: 'AlertTriangleIcon', color: palette.danger,
        },
      ]
    },
  },
  methods: {
    format: formatNumber,
    toNum: toNumber,
    themeOf: getSourceTheme,
    showSourceData(name, key, value) {
      this.sourceData = { name, source: key, data: value }
      this.isModalVisible = true
    },
    toggleSource(key) {
      const index = this.selectedSources.indexOf(key)
      if (index === -1) this.selectedSources.push(key)
      else this.selectedSources.splice(index, 1)
    },
    resetFilters() {
      this.searchQuery = ''
      this.selectedSources = []
      this.filterLateRequests = false
    },
    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc'
      } else {
        this.sortKey = key
        this.sortDir = key === 'name' ? 'asc' : 'desc'
      }
    },
    sourceStyle(key) {
      const { color } = getSourceTheme(key)
      return { '--accent': color, '--accent-soft': tint(color, 0.12) }
    },
    initials(name) {
      const parts = String(name).trim().split(/\s+/).filter(Boolean)
      if (!parts.length) return '?'
      return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase()
    },
    avatarColor(name) {
      let hash = 0
      for (let i = 0; i < name.length; i += 1) hash = (hash * 31 + name.charCodeAt(i)) % 997
      return AVATAR_COLORS[hash % AVATAR_COLORS.length]
    },
    loadWidth(load) {
      return `${Math.max((load / this.maxLoad) * 100, load > 0 ? 3 : 0)}%`
    },
    loadLevel(load) {
      if (load >= 100) return 'high'
      if (load >= 40) return 'mid'
      return 'low'
    },
  },
}
</script>

<style lang="scss">
@import '@/assets/scss/pages/analysis/analysis.scss';
</style>
